import styled from "@emotion/styled";
import { ClickAwayListener, Grow, Popper as MuiPopper, type PopperPlacementType } from "@mui/material";
import { Radius } from "Infrastructure/Themes/Themes";
import { isNoU } from "Infrastructure/Utils/ObjectUtils";
import * as React from "react";

type Props = {
  btnEl: React.JSX.Element;
  translateY?: number;
  noPadding?: boolean;
  noBorder?: boolean;
  boxShadow?: number;
  placement?: PopperPlacementType;
  isOpen?: boolean;
  onOpen?: () => void;
};

const StyledMenu = styled.div<{
  translateY?: number;
  noPadding?: boolean;
  noBorder?: boolean;
  boxShadow?: number;
}>`
  border-radius: ${(props) => props.theme.spacing(Radius.L)};
  border: ${(props) => (props.noBorder ? "unset" : `1px solid ${props.theme.colors.radioBtnDisabled}`)};
  background-color: ${(props) => props.theme.palette.common.white};
  color: ${(props) => props.theme.palette.text.secondary};
  padding: ${(props) => (props.noPadding ? 0 : props.theme.spacing(1, 2))};
  transform: translateY(${(props) => props.translateY ?? 5}px) !important;
  box-shadow: ${(props) => (props.boxShadow ? props.theme.shadows[props.boxShadow] : props.theme.shadows[0])};
`;

export const Popper: React.FunctionComponent<React.PropsWithChildren<Props>> = (props) => {
  const { btnEl, translateY, onOpen, noPadding, noBorder, boxShadow, placement } = props;

  const [isOpen, setIsOpen] = React.useState(!!props.isOpen);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onOpen && !isOpen && onOpen();
    setIsOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setIsOpen(false);
  };

  React.useEffect(() => {
    if (!isNoU(props.isOpen)) {
      setIsOpen(!!props.isOpen);
    }
  }, [props.isOpen]);

  return (
    <div>
      <div onClick={handleToggle} ref={anchorRef}>
        {btnEl}
      </div>

      <MuiPopper open={isOpen} anchorEl={anchorRef.current} placement={placement ?? "bottom-end"} transition style={{ zIndex: 10000 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <StyledMenu translateY={translateY} noPadding={noPadding} noBorder={noBorder} boxShadow={boxShadow}>
              <ClickAwayListener onClickAway={(e) => handleClose(e)}>
                <div>{props.children}</div>
              </ClickAwayListener>
            </StyledMenu>
          </Grow>
        )}
      </MuiPopper>
    </div>
  );
};
